import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../../../../atoms/Icon'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
} from '../../../../../molecules/PropertyPanelStyles'
import {
  OwnerLastChange,
  OwnerChipsGrid,
  OwnerChip,
  OwnerName,
  OwnerActions,
  OwnerActionBtn,
  ShowMoreToggle,
} from './SummaryDashboard.styles'

const VISIBLE_OWNERS_COUNT = 2

export const OwnershipCard: React.FC<{
  land: any
  readOnly: boolean
  ownerPortfolioService?: any
  language: string
}> = ({ land, readOnly, ownerPortfolioService, language }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = React.useState(false)

  const owners = land.owners || []
  const ownerCount = land.owner_nb || owners.length

  const lastChangeDate = land.owner_last_change_date || 'N/A'

  const visibleOwners = expanded
    ? owners
    : owners.slice(0, VISIBLE_OWNERS_COUNT)
  const hiddenCount = owners.length - VISIBLE_OWNERS_COUNT

  const isCompany = (owner: { name: string; type: string }) => {
    return (
      owner.type === 'company' ||
      owner.type === 'institutional' ||
      (owner.name &&
        (owner.name.includes('SA') ||
          owner.name.includes('SARL') ||
          owner.name.includes('FONDATION') ||
          owner.name.includes('ASSOCIATION')))
    )
  }

  const handleGoogleSearch = (name: string) => {
    window.open(
      `https://www.google.com/search?q=${encodeURIComponent(name)}`,
      '_blank'
    )
  }

  const handleLinkedInSearch = (name: string) => {
    window.open(
      `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(name)}`,
      '_blank'
    )
  }

  const handleLocalSearch = (name: string) => {
    window.open(`https://www.local.ch/s/${encodeURIComponent(name)}`, '_blank')
  }

  const handleZefixSearch = (name: string) => {
    const lang = language === 'en' ? 'de' : language
    const cleanedName = name.replace(/ (SA|AG)$/i, '').trim()
    window.open(
      `https://www.zefix.ch/${lang}/search/entity/list?mainSearch=${encodeURIComponent(cleanedName)}`,
      '_blank'
    )
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87" />
              <path d="M16 3.13a4 4 0 010 7.75" />
            </svg>
          </SectionIcon>
          {t('landAnalysis.ownership.title')}
        </SectionTitle>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <OwnerLastChange>
            {t('landAnalysis.ownership.lastChange')}: {lastChangeDate}
          </OwnerLastChange>
          <SectionCount>{ownerCount}</SectionCount>
        </div>
      </SectionHeader>

      {owners.length > 0 ? (
        <SectionContent>
          <OwnerChipsGrid>
            {visibleOwners.map((owner: any) => {
              const name =
                owner.name || t('landAnalysis.ownership.unknownOwner')
              const company = isCompany({
                name,
                type: owner.type || 'private',
              })

              return (
                <OwnerChip key={owner.id || name}>
                  <OwnerName
                    $clickable={!!ownerPortfolioService}
                    onClick={
                      ownerPortfolioService
                        ? () => ownerPortfolioService.openPortfolio(name)
                        : undefined
                    }
                  >
                    {name}
                  </OwnerName>
                  {!readOnly && (
                    <OwnerActions>
                      <OwnerActionBtn
                        $variant="google"
                        title={t('landAnalysis.ownership.searchGoogle')}
                        onClick={() => handleGoogleSearch(name)}
                      >
                        G
                      </OwnerActionBtn>
                      {company ? (
                        <OwnerActionBtn
                          $variant="zefix"
                          title={t('landAnalysis.ownership.searchZefix')}
                          onClick={() => handleZefixSearch(name)}
                        >
                          Z
                        </OwnerActionBtn>
                      ) : (
                        <>
                          <OwnerActionBtn
                            $variant="linkedin"
                            title={t('landAnalysis.ownership.searchLinkedIn')}
                            onClick={() => handleLinkedInSearch(name)}
                          >
                            in
                          </OwnerActionBtn>
                          <OwnerActionBtn
                            $variant="local"
                            title={t('landAnalysis.ownership.searchLocal')}
                            onClick={() => handleLocalSearch(name)}
                          >
                            {t('landAnalysis.ownership.localLabel')}
                          </OwnerActionBtn>
                        </>
                      )}
                    </OwnerActions>
                  )}
                </OwnerChip>
              )
            })}
          </OwnerChipsGrid>

          {!expanded && hiddenCount > 0 && (
            <ShowMoreToggle onClick={() => setExpanded(true)}>
              {t('landAnalysis.summaryTabDashboard.showMoreOwners', {
                count: hiddenCount,
              })}
              <Icon name="chevron-down" size="small" />
            </ShowMoreToggle>
          )}

          {expanded && hiddenCount > 0 && (
            <ShowMoreToggle onClick={() => setExpanded(false)}>
              {t('landAnalysis.summaryTabDashboard.showLess')}
              <Icon name="chevron-up" size="small" />
            </ShowMoreToggle>
          )}
        </SectionContent>
      ) : (
        <SectionContent>
          <div
            style={{
              textAlign: 'center',
              color: '#999',
              padding: '16px',
            }}
          >
            {t('landAnalysis.ownership.noOwnerInfo')}
          </div>
        </SectionContent>
      )}
    </InfoSection>
  )
}
